"use client";

import { useEffect, useState, type ChangeEvent } from "react";
import { useLocation } from "wouter";
import * as Select from '@radix-ui/react-select';
import { ChevronDownIcon, CheckIcon } from 'lucide-react';
import { useOnboardingStore } from "../../lib/onboarding";
import { Input } from "@/components/ui/input";

const roleOptions = [
  "Individual Contributor / Team member",
  "First line manager",
  "Manager of managers / head of department",
  "CEO / CXO",
  "Freelancer",
  "Founder",
  "Other",
] as const;

type RoleOption = (typeof roleOptions)[number];

const OnboardingStep2 = () => {
  const [, setLocation] = useLocation();
  const { role, setRole } = useOnboardingStore();
  const [touched, setTouched] = useState(false);
  const [selectedOption, setSelectedOption] = useState<RoleOption | "">("");
  const [customRole, setCustomRole] = useState("");

  useEffect(() => {
    if (!role) {
      setSelectedOption((prev) => (prev === "Other" ? "Other" : ""));
      setCustomRole("");
      return;
    }

    if (roleOptions.includes(role as RoleOption)) {
      setSelectedOption(role as RoleOption);
      setCustomRole("");
      return;
    }

    setSelectedOption("Other");
    setCustomRole(role ?? "");
  }, [role]);

  const handleRoleSelect = (value: RoleOption) => {
    setSelectedOption(value);
    setTouched(false);

    if (value === "Other") {
      setRole(customRole);
      return;
    }

    setCustomRole("");
    setRole(value);
  };

  const handleCustomRoleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setCustomRole(value);
    setRole(value);
  };

  const handleNext = () => {
    setTouched(true);
    const isValidRole =
      selectedOption &&
      (selectedOption !== "Other" ? Boolean(role) : Boolean(customRole.trim()));

    if (!isValidRole) {
      return;
    }
    if (selectedOption === "Other") {
      setRole(customRole.trim());
    }
    setLocation("/onboarding/step-3");
  };
  return (
    <div className="space-y-8">
      <div className="text-center">
        <h2 className="text-3xl font-bold text-slate-900 font-display">What best describes your current role?</h2>
        <p className="mt-2 text-slate-600">We’ll tailor your coaching track based on where you’re operating today.</p>
      </div>

      <div className="space-y-6">
        <div>
          <label htmlFor="role" className="block text-sm font-medium text-slate-700 font-sans">Current Role</label>
          <Select.Root value={selectedOption} onValueChange={handleRoleSelect}>
            <Select.Trigger id="role" className="mt-1 w-full flex justify-between items-center rounded-md border border-input bg-white px-4 py-2 text-slate-900 shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2">
              <Select.Value placeholder="Select your functional level..." />
              <Select.Icon><ChevronDownIcon className="h-5 w-5 opacity-50" /></Select.Icon>
            </Select.Trigger>
            <Select.Portal>
              <Select.Content className="bg-white rounded-md shadow-lg border p-2 z-50 font-sans">
                <Select.Viewport>
                  {roleOptions.map((option) => (
                    <Select.Item key={option} value={option} className="relative flex items-center px-8 py-2 text-sm rounded-md cursor-pointer outline-none hover:bg-accent/10 focus:bg-accent/10">
                      <Select.ItemText>{option}</Select.ItemText>
                      <Select.ItemIndicator className="absolute left-2 flex items-center text-primary"><CheckIcon className="h-5 w-5" /></Select.ItemIndicator>
                    </Select.Item>
                  ))}
                </Select.Viewport>
              </Select.Content>
            </Select.Portal>
          </Select.Root>
        </div>
        {selectedOption === "Other" && (
          <div className="space-y-2">
            <label htmlFor="custom-role" className="block text-sm font-medium text-slate-700 font-sans">Please specify your role</label>
            <Input
              id="custom-role"
              value={customRole}
              onChange={handleCustomRoleChange}
              placeholder="e.g. Student, Researcher, Solopreneur"
            />
          </div>
        )}
      </div>

      {touched && (!selectedOption || (selectedOption === "Other" && !customRole.trim())) && (
        <p className="text-sm text-red-600">Pick the role that represents you best to continue.</p>
      )}

            <div className="flex justify-between pt-4">
        <button
          type="button"
          onClick={() => setLocation("/onboarding/step-1")}
          className="inline-flex items-center justify-center px-6 py-2 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary font-display"
        >
          Back
        </button>
        <button
          type="button"
          onClick={handleNext}
          className="inline-flex items-center justify-center px-6 py-2 border border-transparent text-base font-medium rounded-md shadow-sm bg-primary !text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary font-display"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default OnboardingStep2;
